import { useState } from "react";
import { Show, TextField } from "@refinedev/antd";
import { useCustomMutation, useGetIdentity, useShow, useUpdate } from "@refinedev/core";
import { Typography, Button, notification, Spin, Card, Space, Row, Col, Descriptions, Tag, Modal, Form, Input, Alert } from "antd";
import { TOKEN_KEY } from "../../authProvider";
import { IconDisplay } from "../../components/IconDisplay";

const { Title } = Typography;

const PERFORMANCE_LEVEL_LABELS: Record<string, string> = {
    foundation: "Foundation",
    development: "Development",
    academy: "Academy",
    performance: "Performance",
};

const POSITION_LABELS: Record<string, string> = {
    GK: "Goalkeeper", CB: "Centre back", LB: "Left back", RB: "Right back",
    "LB/RB": "Full back", CDM: "Defensive midfielder", CM: "Central midfielder",
    CAM: "Attacking midfielder", LW: "Left winger", RW: "Right winger", ST: "Striker",
};

const TRAINING_DURATION_LABELS: Record<string, string> = {
    none: "No prior training", under6m: "Under 6 months", "6to12m": "6 to 12 months",
    "1to2y": "1 to 2 years", "2plus": "2 years or more",
};

const GENDER_LABELS: Record<string, string> = { MALE: "Male", FEMALE: "Female", OTHER: "Other" };

const labelOr = (map: Record<string, string>, value: any): string =>
    value ? (map[value] ?? String(value)) : "N/A";

const withUnit = (value: any, unit: string): string => {
    // Heights/weights use 0 (and profile's "0.0" default) as an unset placeholder.
    if (value === null || value === undefined || value === "" || Number(value) === 0) return "N/A";
    return `${value} ${unit}`;
};

// A managed player is a "Child"; a real account is a "Parent" only if it manages
// at least one child, otherwise a regular "User".
const classifyUserType = (user: any): string => {
    if (user?.accountType === "MANAGED_PLAYER") return "Child";
    return user?.childCount > 0 ? "Parent" : "User";
};

export const UserShow = () => {
    const [passwordForm] = Form.useForm();
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);
    const { data: identity } = useGetIdentity<{ email?: string; role?: string }>();
    const { query } = useShow({});
    const { mutate: updateStaus, isLoading: statusLoading } = useUpdate({
        resource: "user",
        mutationMode: "optimistic",
        mutationOptions: {
            retry: 3,
            onSuccess: (data) => {
                notification.success({
                    message: "Success",
                    description: data?.data?.message,
                })
            },
            onError: (error) => {
                console.error("Error updating user status:", error);
            },
        }
    });

    const { mutate: customMutate, isLoading: removingPhone } = useCustomMutation({});
    const { mutate: generateLink, isLoading: generatingLink } = useCustomMutation({});
    const { mutate: overridePassword, isLoading: overridingPassword } = useCustomMutation({});
    const { mutate: impersonate, isLoading: impersonating } = useCustomMutation({});

    const { data, isFetching } = query;
    const user = data?.data?.data;
    const canOverridePassword = identity?.email?.toLowerCase() === "admin@gymlive.app"
        && user?.accountType !== "MANAGED_PLAYER";

    const handleOverridePassword = (values: { newPassword: string; confirmation: string }) => {
        const token = localStorage.getItem(TOKEN_KEY);
        const apiUrl = import.meta.env.VITE_API_URL;

        overridePassword(
            {
                url: `${apiUrl}/admin/user/${user.id}/password-override`,
                method: "post",
                values: { newPassword: values.newPassword },
                config: { headers: { Authorization: `Bearer ${token}` } },
            },
            {
                onSuccess: (resp: any) => {
                    setPasswordModalOpen(false);
                    passwordForm.resetFields();
                    notification.success({
                        message: "Password overridden",
                        description: resp?.data?.message || "The user can now sign in with the new password.",
                    });
                },
                onError: (error: any) => {
                    notification.error({
                        message: "Password override failed",
                        description: error?.response?.data?.error || error?.message || "Please try again.",
                    });
                },
            },
        );
    };

    const handleToggleStatus = (id: string, currentStatus: boolean) => {
        // console.log('Current status:', currentStatus);
        updateStaus({
            id, 
            values: { isActive: !currentStatus },
        });
    }

    const handleRemovePhone = (id: string) => {
        if (!id) return;
        const confirmed = window.confirm("Remove this user's phone number?");
        if (!confirmed) return;

        const token = localStorage.getItem(TOKEN_KEY);
        const apiUrl = import.meta.env.VITE_API_URL;

        customMutate(
            {
                url: `${apiUrl}/admin/user/${id}/phone`,
                method: "delete",
                values: {},
                config: {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            },
            {
                onSuccess: (resp: any) => {
                    notification.success({
                        message: "Success",
                        description: resp?.data?.message || "User phone removed successfully!",
                    });
                    // Refetch user details
                    // @ts-ignore - refine query exposes refetch
                    query?.refetch && query.refetch();
                },
                onError: (error: any) => {
                    notification.error({
                        message: "Error removing phone",
                        description: error?.message || "Please try again.",
                    });
                },
            }
        );
    }

    const handleGenerateOnboardingLink = (id: string) => {
        if (!id) return;

        const token = localStorage.getItem(TOKEN_KEY);
        const apiUrl = import.meta.env.VITE_API_URL;

        generateLink(
            {
                url: `${apiUrl}/admin/user/${id}/onboarding-link`,
                method: "post",
                values: {},
                config: {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            },
            {
                onSuccess: async (resp: any) => {
                    const url: string | undefined = resp?.data?.data?.url;
                    if (!url) {
                        notification.error({
                            message: "No link returned",
                            description: "The server did not return an onboarding link.",
                        });
                        return;
                    }
                    let copied = false;
                    try {
                        await navigator.clipboard.writeText(url);
                        copied = true;
                    } catch {
                        // Clipboard can be blocked (permissions/insecure context) —
                        // fall back to showing the URL so the admin can copy manually.
                    }
                    notification.success({
                        message: copied ? "Onboarding link copied to clipboard" : "Onboarding link generated",
                        description: url,
                        duration: 0,
                    });
                },
                onError: (error: any) => {
                    notification.error({
                        message: "Error generating onboarding link",
                        description: error?.message || "Please try again.",
                    });
                },
            }
        );
    }

    const handleImpersonate = () => {
        const token = localStorage.getItem(TOKEN_KEY);
        const apiUrl = import.meta.env.VITE_API_URL;
        impersonate(
            {
                url: `${apiUrl}/admin/user/${user.id}/impersonate`,
                method: "post",
                values: {},
                config: { headers: { Authorization: `Bearer ${token}` } },
            },
            {
                onSuccess: (response: any) => {
                    const redirectUrl = response?.data?.redirectUrl;
                    if (redirectUrl) window.location.assign(redirectUrl);
                    else notification.error({ message: "No redirect URL returned" });
                },
                onError: (error: any) => notification.error({
                    message: "Could not log in as user",
                    description: error?.response?.data?.error || error?.message || "Please try again.",
                }),
            },
        );
    };

    return (
        <Show isLoading={isFetching} >
            <Card style={{ minWidth: 300 }}>
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 20
                }}>
                    <Title level={3}>User Details</Title>
                </div>

                <Row gutter={[24, 24]}>
                    <Col span={24} style={{ display: "flex", justifyContent: "center" }}>
                        <Card variant="borderless" style={{ marginBottom: 16, width: "fit-content", padding: 30 }}>
                            <Space direction="vertical" align="center" style={{ width: '100%' }}>
                                <IconDisplay
                                    iconURL={user?.profilePictureURL || ""}
                                    width={120}
                                    height={110}
                                    borderRadius="50%"
                                />
                                {identity?.role === "SUPER_ADMIN" && (
                                    <Button
                                        type="primary"
                                        loading={impersonating}
                                        disabled={user?.accountType === "MANAGED_PLAYER" || !user?.isActive}
                                        onClick={handleImpersonate}
                                    >
                                        Login as this user
                                    </Button>
                                )}
                                {user?.isActive ? (
                                    <Button
                                        danger
                                        style={{ width: 150, marginTop: 10 }}
                                        onClick={() => handleToggleStatus(user?.id, user?.isActive)}
                                    >
                                        {statusLoading ? <Spin /> : 'Deactivate account'}
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={() => handleToggleStatus(user?.id, user?.isActive)}
                                        style={{
                                            borderColor: 'green',
                                            color: 'green',
                                            width: 150,
                                        }}
                                    >
                                        {statusLoading ? <Spin /> : 'Activate account'}
                                    </Button>
                                )}
                                <Button
                                    type="primary"
                                    style={{ width: 150 }}
                                    loading={generatingLink}
                                    onClick={() => handleGenerateOnboardingLink(user?.id)}
                                >
                                    Onboarding link
                                </Button>
                                {canOverridePassword ? (
                                    <Button
                                        danger
                                        style={{ width: 150 }}
                                        onClick={() => setPasswordModalOpen(true)}
                                    >
                                        Override password
                                    </Button>
                                ) : null}
                            </Space>
                        </Card>
                    </Col>

                    <Col span={24} style={{ display: "flex", justifyContent: "center" }}>
                        {/* <Card variant="borderless"> */}
                            <Space direction="vertical" style={{ width: '70%', minWidth: 300 }}>
                                <Descriptions 
                                    bordered 
                                    column={1}
                                    labelStyle={{ 
                                        fontWeight: 'bold',
                                    }}
                                >
                                    <Descriptions.Item label="Full Name">
                                        <TextField value={user?.fullName} />
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Username">
                                        <TextField value={user?.username} />
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Email">
                                        <TextField value={user?.email} />
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Phone">
                                        <Space>
                                            <TextField value={user?.phone || "N/A"} />
                                            {user?.phone ? (
                                                <Button danger size="small" loading={removingPhone} onClick={() => handleRemovePhone(user?.id)}>
                                                    Remove
                                                </Button>
                                            ) : null}
                                        </Space>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Status">
                                        <TextField value={user?.isActive ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>} />
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Bio">
                                        <TextField value={user?.bio ? user?.bio : "N/A"} />
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Type">
                                        <TextField value={classifyUserType(user)} />
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Gender">
                                        <TextField value={labelOr(GENDER_LABELS, user?.gender)} />
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Date of birth">
                                        <TextField value={user?.birthdate ? user?.birthdate : "N/A"} />
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Country">
                                        <TextField value={user?.country ? user?.country : "N/A"} />
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Height">
                                        <TextField value={withUnit(user?.height, "cm")} />
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Weight">
                                        <TextField value={withUnit(user?.weight, "kg")} />
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Father's height">
                                        <TextField value={withUnit(user?.fatherHeightCm, "cm")} />
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Mother's height">
                                        <TextField value={withUnit(user?.motherHeightCm, "cm")} />
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Club">
                                        <TextField value={user?.club ? user?.club : "N/A"} />
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Position">
                                        <TextField value={labelOr(POSITION_LABELS, user?.position)} />
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Training experience">
                                        <TextField value={labelOr(TRAINING_DURATION_LABELS, user?.trainingDuration)} />
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Performance group">
                                        <TextField value={labelOr(PERFORMANCE_LEVEL_LABELS, user?.performanceLevel)} />
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Training slot">
                                        <TextField value={user?.trainingSlot ? user?.trainingSlot : "N/A"} />
                                    </Descriptions.Item>
                                </Descriptions>
                            </Space>
                        {/* </Card> */}
                    </Col>
                </Row>
            </Card>
            <Modal
                title="Override user password"
                open={passwordModalOpen}
                okText="Override password"
                okButtonProps={{ danger: true, loading: overridingPassword }}
                cancelButtonProps={{ disabled: overridingPassword }}
                closable={!overridingPassword}
                maskClosable={false}
                onCancel={() => {
                    setPasswordModalOpen(false);
                    passwordForm.resetFields();
                }}
                onOk={() => passwordForm.submit()}
            >
                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                    <Alert
                        type="warning"
                        showIcon
                        message={`This changes the password for ${user?.email ?? "this user"}`}
                        description="All of their active sessions will be signed out immediately. Share the new password through a secure channel."
                    />
                    <Form form={passwordForm} layout="vertical" onFinish={handleOverridePassword}>
                        <Form.Item
                            name="newPassword"
                            label="New password"
                            rules={[
                                { required: true, message: "Enter a new password" },
                                { min: 8, message: "Use at least 8 characters" },
                                { max: 72, message: "Use no more than 72 characters" },
                            ]}
                        >
                            <Input.Password autoComplete="new-password" placeholder="At least 8 characters" />
                        </Form.Item>
                        <Form.Item
                            name="confirmation"
                            label={`Type ${user?.email ?? "the user's email"} to confirm`}
                            dependencies={["newPassword"]}
                            rules={[
                                { required: true, message: "Confirm the user's email" },
                                {
                                    validator: (_, value) => value === user?.email
                                        ? Promise.resolve()
                                        : Promise.reject(new Error("Email does not match")),
                                },
                            ]}
                        >
                            <Input autoComplete="off" />
                        </Form.Item>
                    </Form>
                </Space>
            </Modal>
        </Show>
    )
}
